import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, ExternalLink, Sparkles, FileText, Lock, Plus, Save, Trash2, Upload } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useTracker } from '@/lib/tracker';
import {
  COST_KIND,
  VENDOR_STATUS,
  money,
  useCategory,
  vendorsKey,
  type CategoryDetail,
  type Cost,
  type CostKind,
  type Matrix,
  type Vendor,
  type VendorStatus,
} from '@/lib/vendors';
import { downloadFrom } from '@/lib/reports';
import { PrefillScores } from '@/components/ai/Prefill';
import type { Program } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert, Badge, Card, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { ConfirmDialog, RESTORE_NOTE } from '@/components/ui/confirm';
import { useToast } from '@/components/ui/toast';
import { UploadDialog } from '@/components/documents/UploadDialog';

type Tab = 'vendors' | 'scores' | 'compare' | 'setup';

export function CategoryPage() {
  const program = useOutletContext<Program>();
  const { categoryId = '' } = useParams();
  const q = useCategory(program.id, categoryId);
  const [params, setParams] = useSearchParams();
  if (q.isLoading) return <Spinner className="h-64" />;
  if (!q.data)
    return (
      <div className="mx-auto max-w-[900px] px-6 py-10">
        <Alert tone="bad" title="Category not found" />
      </div>
    );
  const c = q.data;
  const tabs: Array<[Tab, string]> = [
    ['vendors', 'Vendors'],
    ...(c.isEvaluator ? ([['scores', 'My scores']] as Array<[Tab, string]>) : []),
    ['compare', 'Comparison'],
    ...(c.canManage ? ([['setup', 'Setup']] as Array<[Tab, string]>) : []),
  ];
  const tab = (params.get('tab') as Tab) || (c.isEvaluator && !c.iSubmitted ? 'scores' : 'vendors');
  const setTab = (t: Tab) => setParams({ tab: t }, { replace: true });

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <Link
        to={`/p/${program.id}/vendors`}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" /> Vendors
      </Link>
      <h1 className="text-xl font-semibold tracking-tight">{c.name}</h1>
      <p className="mb-4 text-sm text-muted">
        {c.evaluators.filter((e) => e.submitted).length} of {c.evaluators.length} evaluators submitted
        {c.evaluators.length
          ? `: ${c.evaluators.map((e) => `${e.name}${e.submitted ? ' (submitted)' : ''}`).join(', ')}`
          : ''}
      </p>
      <div className="mb-5 flex gap-1 border-b border-border" role="tablist">
        {tabs.map(([t, label]) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              '-mb-px border-b-2 px-3 pb-2 pt-1 text-sm',
              tab === t ? 'border-brand font-medium text-text' : 'border-transparent text-muted hover:text-text',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'vendors' && <VendorsTab program={program} c={c} />}
      {tab === 'scores' && c.isEvaluator && <ScoresTab program={program} c={c} />}
      {tab === 'compare' && <CompareTab program={program} c={c} />}
      {tab === 'setup' && c.canManage && <SetupTab program={program} c={c} />}
    </div>
  );
}

function VendorsTab({ program, c }: { program: Program; c: CategoryDetail }) {
  const [editing, setEditing] = useState<Partial<Vendor> | null>(null);
  const [uploadFor, setUploadFor] = useState<Vendor | null>(null);
  const qc = useQueryClient();
  return (
    <div className="space-y-3">
      {c.canManage && (
        <Button onClick={() => setEditing({ status: 'LONG_LIST', costs: [] })}>
          <Plus /> Add vendor
        </Button>
      )}
      {!c.vendors.length && <p className="text-sm text-muted">No vendors yet.</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {c.vendors.map((v) => (
          <Card key={v.id} className="p-4 text-sm">
            <div className="flex items-start gap-2">
              <h3 className="flex-1 font-semibold">{v.name}</h3>
              <Badge tone={VENDOR_STATUS[v.status].tone}>{VENDOR_STATUS[v.status].label}</Badge>
            </div>
            {v.contact && <p className="mt-1 text-xs text-muted">{v.contact}</p>}
            {v.website && (
              <a
                href={v.website}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-xs text-brand-text hover:underline"
              >
                <ExternalLink className="size-3" /> {v.website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {v.notes && <p className="mt-2 whitespace-pre-wrap">{v.notes}</p>}
            {v.costs.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs">
                {v.costs.map((k, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="text-muted">{k.label}</span>
                    <span className="tabular-nums">
                      {k.kind === 'PER_LOAN_MONTH' ? `$${Number(k.amount).toFixed(2)}` : money(k.amount)}{' '}
                      {COST_KIND[k.kind].toLowerCase()}
                      {Number(k.annualEscalationPct) > 0 ? `, +${Number(k.annualEscalationPct)}%/yr` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {v.documents.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {v.documents.map((d) => (
                  <Link
                    key={d.id}
                    to={`/p/${program.id}/documents/${d.id}`}
                    className="flex items-center gap-1 text-xs hover:underline"
                  >
                    <FileText className="size-3 text-muted" /> {d.title}
                  </Link>
                ))}
              </div>
            )}
            {c.canManage && (
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setEditing(v)}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setUploadFor(v)}>
                  <Upload /> File material
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
      {editing && <VendorDialog program={program} c={c} v={editing} onClose={() => setEditing(null)} />}
      {uploadFor && (
        <UploadDialog
          program={program}
          open
          target={{ vendorId: uploadFor.id, label: uploadFor.name }}
          onClose={() => setUploadFor(null)}
          onUploaded={() => void qc.invalidateQueries({ queryKey: vendorsKey(program.id) })}
        />
      )}
    </div>
  );
}

function VendorDialog({
  program,
  c,
  v,
  onClose,
}: {
  program: Program;
  c: CategoryDetail;
  v: Partial<Vendor>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState({
    name: v.name ?? '',
    status: (v.status ?? 'LONG_LIST') as VendorStatus,
    contact: v.contact ?? '',
    website: v.website ?? '',
    notes: v.notes ?? '',
  });
  const [costs, setCosts] = useState<Cost[]>(v.costs ?? []);
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: f.name,
        status: f.status,
        contact: f.contact || null,
        website: f.website || null,
        notes: f.notes || null,
      };
      const id = v.id
        ? (await api.patch(`/api/programs/${program.id}/vendors/${v.id}`, body), v.id)
        : (await api.post<{ id: string }>(`/api/programs/${program.id}/vendors/categories/${c.id}/vendors`, body)).id;
      await api.put(
        `/api/programs/${program.id}/vendors/${id}/costs`,
        costs.map((k) => ({
          label: k.label,
          kind: k.kind,
          amount: k.amount,
          annualEscalationPct: Number(k.annualEscalationPct || 0),
          note: k.note || null,
        })),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vendorsKey(program.id) });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const setCost = (i: number, p: Partial<Cost>) => setCosts(costs.map((k, j) => (j === i ? { ...k, ...p } : k)));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/programs/${program.id}/vendors/${v.id}`),
    onSuccess: () => {
      toast.ok('Vendor deleted.');
      void qc.invalidateQueries({ queryKey: vendorsKey(program.id) });
      onClose();
    },
    onError: (e) => {
      setConfirmDelete(false);
      toast.error(errorMessage(e));
    },
  });
  return (
    <>
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this vendor?"
        description={`Only before anyone has scored it; after that, set its status to Declined. ${RESTORE_NOTE}`}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
        loading={remove.isPending}
      />
      <Dialog
        open
        onClose={onClose}
        wide
        title={v.id ? v.name! : 'Add vendor'}
        footer={
          <>
            {v.id && (
              <Button variant="ghost" className="mr-auto" onClick={() => setConfirmDelete(true)}>
                <Trash2 /> Delete vendor
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!f.name.trim()}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <Field label="Name" htmlFor="vd-name">
              <Input id="vd-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </Field>
            <Field label="Status" htmlFor="vd-status">
              <Select
                id="vd-status"
                value={f.status}
                onChange={(e) => setF({ ...f, status: e.target.value as VendorStatus })}
              >
                {(Object.keys(VENDOR_STATUS) as VendorStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {VENDOR_STATUS[s].label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Business contact" htmlFor="vd-contact">
              <Input id="vd-contact" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} />
            </Field>
            <Field label="Website" htmlFor="vd-web">
              <Input
                id="vd-web"
                value={f.website}
                placeholder="https://"
                onChange={(e) => setF({ ...f, website: e.target.value })}
              />
            </Field>
            <Field label="Notes" htmlFor="vd-notes">
              <Textarea
                id="vd-notes"
                rows={4}
                value={f.notes}
                onChange={(e) => setF({ ...f, notes: e.target.value })}
              />
            </Field>
          </div>
          <div className="space-y-2">
            <p className="text-[13px] font-medium">Proposal costs</p>
            <p className="text-xs text-muted">
              Per-loan pricing is a rate; the comparison multiplies it by a total loan count you enter there.
            </p>
            {costs.map((k, i) => (
              <div key={i} className="grid grid-cols-[1fr_120px] gap-2 rounded-md border border-border p-2">
                <Input
                  aria-label="Cost label"
                  placeholder="License"
                  value={k.label}
                  onChange={(e) => setCost(i, { label: e.target.value })}
                />
                <Select
                  aria-label="Cost kind"
                  value={k.kind}
                  onChange={(e) => setCost(i, { kind: e.target.value as CostKind })}
                >
                  {(Object.keys(COST_KIND) as CostKind[]).map((x) => (
                    <option key={x} value={x}>
                      {COST_KIND[x]}
                    </option>
                  ))}
                </Select>
                <Input
                  aria-label="Amount"
                  inputMode="decimal"
                  placeholder="Amount (USD)"
                  value={k.amount}
                  onChange={(e) => setCost(i, { amount: e.target.value })}
                />
                <div className="flex gap-1">
                  <Input
                    aria-label="Yearly increase percent"
                    inputMode="decimal"
                    placeholder="+%/yr"
                    value={k.annualEscalationPct}
                    onChange={(e) => setCost(i, { annualEscalationPct: e.target.value })}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove cost"
                    onClick={() => setCosts(costs.filter((_, j) => j !== i))}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setCosts([...costs, { label: '', kind: 'ANNUAL', amount: '', annualEscalationPct: '0', note: null }])
              }
            >
              <Plus /> Add a cost line
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

function ScoresTab({ program, c }: { program: Program; c: CategoryDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const vendors = c.vendors.filter((v) => v.status !== 'DECLINED');
  const [scores, setScores] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(c.myScores.map((s) => [`${s.vendorId}|${s.criterionId}`, s.score])),
  );
  /** Scores confirmed from a suggestion carry the run id; a hand change clears it. */
  const [runs, setRuns] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(c.myScores.map((s) => [`${s.vendorId}|${s.criterionId}`, s.aiRunId ?? null])),
  );
  const [confirm, setConfirm] = useState(false);
  const url = `/api/programs/${program.id}/vendors/categories/${c.id}`;
  const save = useMutation({
    mutationFn: (x: { vendorId: string; criterionId: string; score: number | null; aiRunId?: string }) =>
      api.put(`${url}/scores`, { scores: [x] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = useMutation({
    mutationFn: () => api.post(`${url}/submit`),
    onSuccess: () => {
      toast.ok('Submitted. You can now see the comparison.');
      setConfirm(false);
      void qc.invalidateQueries({ queryKey: vendorsKey(program.id) });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const total = vendors.length * c.criteria.length;
  const done = vendors.reduce((n, v) => n + c.criteria.filter((k) => scores[`${v.id}|${k.id}`]).length, 0);

  return (
    <div className="space-y-4">
      {c.iSubmitted ? (
        <Alert tone="ok" title="Submitted">
          Your scores are locked and included in the comparison. Ask BlackWolf to reopen them if something changes.
        </Alert>
      ) : (
        <Alert tone="info" title="Score independently">
          Other evaluators' scores stay hidden until you submit, and yours stay hidden from them until they submit.
          Scores save as you go; nobody sees them before you submit.
        </Alert>
      )}
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-[13px]">
          <thead className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-2 font-medium">Criterion</th>
              {vendors.map((v) => (
                <th key={v.id} className="px-2 py-2 font-medium">
                  <div>{v.name}</div>
                  {!c.iSubmitted && v.documents.length > 0 && (
                    <div className="mt-1 normal-case tracking-normal">
                      <PrefillScores
                        program={program}
                        categoryUrl={url}
                        vendor={v}
                        current={(criterionId) => scores[`${v.id}|${criterionId}`] ?? null}
                        onUse={(criterionId, score, runId) => {
                          const key = `${v.id}|${criterionId}`;
                          setScores((x) => ({ ...x, [key]: score }));
                          setRuns((x) => ({ ...x, [key]: runId }));
                          save.mutate({ vendorId: v.id, criterionId, score, aiRunId: runId });
                        }}
                      />
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {c.criteria.map((k) => (
              <tr key={k.id} className="border-b border-border last:border-0">
                <td className="px-4 py-2">
                  {k.name} <span className="text-xs text-muted">{k.weight}%</span>
                </td>
                {vendors.map((v) => {
                  const key = `${v.id}|${k.id}`;
                  return (
                    <td key={v.id} className="px-2 py-1.5">
                      <Select
                        aria-label={`${v.name}: ${k.name}`}
                        className="h-8 w-20"
                        value={scores[key] ?? ''}
                        disabled={c.iSubmitted}
                        onChange={(e) => {
                          const score = e.target.value ? Number(e.target.value) : null;
                          setScores({ ...scores, [key]: score });
                          setRuns({ ...runs, [key]: null });
                          save.mutate({ vendorId: v.id, criterionId: k.id, score });
                        }}
                      >
                        <option value="">-</option>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Select>
                      {runs[key] && (
                        <span title="Confirmed from an assistant suggestion" className="ml-1 text-brand-text">
                          <Sparkles className="inline size-3.5" />
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {!c.iSubmitted && (
        <div className="flex items-center gap-3">
          <Button onClick={() => setConfirm(true)} disabled={!total || done < total}>
            Submit my scores
          </Button>
          <span className="text-xs text-muted">
            {done} of {total} scored. 1 is poor, 5 is excellent.
          </span>
        </div>
      )}
      <Dialog
        open={confirm}
        onClose={() => setConfirm(false)}
        title="Submit your scores?"
        description="They lock, and you will see the other evaluators' submitted scores."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={() => submit.mutate()} loading={submit.isPending}>
              Submit
            </Button>
          </>
        }
      >
        <span />
      </Dialog>
    </div>
  );
}

function CompareTab({ program, c }: { program: Program; c: CategoryDetail }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [weights, setWeights] = useState<Record<string, number>>(() =>
    Object.fromEntries(c.criteria.map((k) => [k.id, k.weight])),
  );
  const [years, setYears] = useState(5);
  const [loanCount, setLoanCount] = useState('');
  const changed = c.criteria.some((k) => weights[k.id] !== k.weight);
  const qs = new URLSearchParams({
    years: String(years),
    ...(loanCount ? { loanCount } : {}),
    ...(changed ? { weights: JSON.stringify(weights) } : {}),
  });
  const q = useQuery({
    queryKey: [...vendorsKey(program.id), 'matrix', c.id, qs.toString()],
    queryFn: () => api.get<Matrix>(`/api/programs/${program.id}/vendors/categories/${c.id}/matrix?${qs}`),
    placeholderData: (prev) => prev,
    retry: false,
  });
  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  const saveWeights = useMutation({
    mutationFn: () => api.post(`/api/programs/${program.id}/vendors/categories/${c.id}/weights`, weights),
    onSuccess: () => {
      toast.ok('Weights saved.');
      void qc.invalidateQueries({ queryKey: vendorsKey(program.id) });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  // Reset the sliders only when the saved weights themselves change, not on every refetch.
  const savedWeights = JSON.stringify(c.criteria.map((k) => [k.id, k.weight]));
  useEffect(() => setWeights(Object.fromEntries(JSON.parse(savedWeights) as Array<[string, number]>)), [savedWeights]);

  if (!c.seesResults)
    return (
      <Alert tone="info" title={c.isEvaluator ? 'Submit your scores to see the comparison' : 'No results yet'}>
        <Lock className="mr-1 inline size-4" />
        {c.isEvaluator
          ? "The comparison opens once you submit, so nobody's view is anchored by others."
          : 'The comparison appears once an evaluator submits.'}
      </Alert>
    );
  const m = q.data;
  return (
    <div className="space-y-4">
      <Card className="grid gap-4 p-4 lg:grid-cols-[1fr_260px]">
        <div className="space-y-2">
          <p className="text-[13px] font-medium">
            Weights{' '}
            <span className={cn('text-xs', Math.abs(total - 100) > 0.01 ? 'text-warn' : 'text-muted')}>
              (total {total}%)
            </span>
          </p>
          {c.criteria.map((k) => (
            <label key={k.id} className="grid grid-cols-[180px_1fr_44px] items-center gap-3 text-[13px]">
              <span className="truncate">{k.name}</span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={weights[k.id] ?? 0}
                onChange={(e) => setWeights({ ...weights, [k.id]: Number(e.target.value) })}
                aria-label={`Weight for ${k.name}`}
                className="accent-[var(--brand)]"
              />
              <span className="text-right tabular-nums">{weights[k.id]}%</span>
            </label>
          ))}
          <div className="flex gap-2 pt-1">
            {changed && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setWeights(Object.fromEntries(c.criteria.map((k) => [k.id, k.weight])))}
              >
                Reset
              </Button>
            )}
            {changed && c.canManage && (
              <Button
                size="sm"
                variant="secondary"
                disabled={Math.abs(total - 100) > 0.01}
                onClick={() => saveWeights.mutate()}
              >
                <Save /> Save these weights
              </Button>
            )}
          </div>
        </div>
        <div className="space-y-3">
          <Field label="Years of ownership" htmlFor="cmp-years">
            <Input
              id="cmp-years"
              type="number"
              min={1}
              max={15}
              value={years}
              onChange={(e) => setYears(Math.max(1, Math.min(15, Number(e.target.value) || 1)))}
            />
          </Field>
          <Field
            label="Loans serviced (total count)"
            htmlFor="cmp-loans"
            hint="For per-loan pricing. An aggregate count only."
          >
            <Input
              id="cmp-loans"
              inputMode="numeric"
              value={loanCount}
              onChange={(e) => setLoanCount(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() =>
              downloadFrom(
                `/api/programs/${program.id}/vendors/categories/${c.id}/matrix/export?${qs}`,
                'vendor-evaluation-matrix.xlsx',
              ).catch((e) => toast.error(errorMessage(e)))
            }
          >
            <Download /> Export the matrix
          </Button>
        </div>
      </Card>
      {!m ? (
        <Spinner className="h-32" />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-[13px]">
            <thead className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Rank</th>
                <th className="px-2 py-2 font-medium">Vendor</th>
                {m.criteria.map((k) => (
                  <th key={k.id} className="px-2 py-2 font-medium">
                    {k.name}
                  </th>
                ))}
                <th className="px-2 py-2 font-medium">Weighted</th>
                <th className="px-4 py-2 text-right font-medium">Cost over {m.years} yr</th>
              </tr>
            </thead>
            <tbody>
              {[...m.rows]
                .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99))
                .map((r) => (
                  <tr
                    key={r.vendorId}
                    className={cn('border-b border-border last:border-0', r.status === 'DECLINED' && 'text-muted')}
                  >
                    <td className="px-4 py-2 font-semibold tabular-nums">{r.rank ?? '-'}</td>
                    <td className="px-2 py-2">
                      {r.name}
                      {r.status === 'DECLINED' && <span className="ml-1 text-xs">(declined)</span>}
                    </td>
                    {r.perCriterion.map((p) => (
                      <td key={p.criterionId} className="px-2 py-2 tabular-nums">
                        {p.mean ?? '-'}
                        {p.spread !== null && p.spread >= 2 && (
                          <Badge tone="warn" className="ml-1" title={`Evaluators differ by ${p.spread} points`}>
                            spread {p.spread}
                          </Badge>
                        )}
                      </td>
                    ))}
                    <td className="px-2 py-2 font-semibold tabular-nums">{r.weightedTotal ?? '-'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {r.tco ? money(r.tco.total) : <span className="text-muted">No pricing</span>}
                      {r.tco?.incomplete && <div className="text-xs text-warn">Needs the loan count</div>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <p className="border-t border-border px-4 py-2 text-xs text-muted">
            From {m.evaluatorsSubmitted} submitted evaluator{m.evaluatorsSubmitted === 1 ? '' : 's'}. Scores are means
            on a 1 to 5 scale; weighted is the weight-averaged mean. A spread of 2 or more flags evaluators who
            disagree.
          </p>
        </Card>
      )}
    </div>
  );
}

function SetupTab({ program, c }: { program: Program; c: CategoryDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const tracker = useTracker(program.id);
  const [criteria, setCriteria] = useState(c.criteria.map((k) => ({ ...k })));
  const [evaluators, setEvaluators] = useState<string[]>(c.evaluators.map((e) => e.userId));
  const total = useMemo(() => criteria.reduce((a, k) => a + (Number(k.weight) || 0), 0), [criteria]);
  const url = `/api/programs/${program.id}/vendors/categories/${c.id}`;
  const saveCriteria = useMutation({
    mutationFn: () =>
      api.put(
        `${url}/criteria`,
        criteria.map((k) => ({ ...(k.id ? { id: k.id } : {}), name: k.name, weight: Number(k.weight) })),
      ),
    onSuccess: () => {
      toast.ok('Criteria saved.');
      void qc.invalidateQueries({ queryKey: vendorsKey(program.id) });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const [reopening, setReopening] = useState<{ userId: string; name: string } | null>(null);
  const [reason, setReason] = useState('');
  const reopen = useMutation({
    mutationFn: () => api.post(`${url}/reopen`, { evaluatorId: reopening!.userId, reason }),
    onSuccess: () => {
      toast.ok(`${reopening!.name} can change their scores again.`);
      setReopening(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: vendorsKey(program.id) });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const saveEvaluators = useMutation({
    mutationFn: () => api.put(`${url}/evaluators`, { userIds: evaluators }),
    onSuccess: () => {
      toast.ok('Evaluators saved.');
      void qc.invalidateQueries({ queryKey: vendorsKey(program.id) });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card className="space-y-2 p-4">
        <h2 className="text-sm font-semibold">
          Criteria{' '}
          <span className={cn('text-xs font-normal', Math.abs(total - 100) > 0.01 ? 'text-warn' : 'text-muted')}>
            (weights total {total}%)
          </span>
        </h2>
        {criteria.map((k, i) => (
          <div key={k.id ?? i} className="flex gap-2">
            <Input
              aria-label="Criterion"
              value={k.name}
              onChange={(e) => setCriteria(criteria.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
            />
            <Input
              aria-label="Weight percent"
              className="w-20"
              inputMode="decimal"
              value={String(k.weight)}
              onChange={(e) =>
                setCriteria(criteria.map((x, j) => (j === i ? { ...x, weight: Number(e.target.value) || 0 } : x)))
              }
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label="Remove criterion"
              onClick={() => setCriteria(criteria.filter((_, j) => j !== i))}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setCriteria([...criteria, { id: '', name: '', weight: 0 }])}>
            <Plus /> Add criterion
          </Button>
          <Button
            size="sm"
            className="ml-auto"
            onClick={() => saveCriteria.mutate()}
            loading={saveCriteria.isPending}
            disabled={Math.abs(total - 100) > 0.01 || criteria.some((k) => !k.name.trim())}
          >
            Save criteria
          </Button>
        </div>
      </Card>
      <Card className="space-y-2 p-4">
        <h2 className="text-sm font-semibold">Evaluators</h2>
        <p className="text-xs text-muted">
          Who scores this category. Their scores stay blind to each other until each submits.
        </p>
        {tracker.data?.members
          .filter((m) => m.role !== 'VENDOR')
          .map((m) => (
            <Checkbox
              key={m.userId}
              id={`ev-${m.userId}`}
              checked={evaluators.includes(m.userId)}
              onChange={(on) =>
                setEvaluators(on ? [...evaluators, m.userId] : evaluators.filter((x) => x !== m.userId))
              }
              label={m.name}
              hint={m.role.toLowerCase()}
            />
          ))}
        <Button size="sm" onClick={() => saveEvaluators.mutate()} loading={saveEvaluators.isPending}>
          Save evaluators
        </Button>
        {c.evaluators.some((e) => e.submitted) && (
          <div className="border-t border-border pt-3">
            <p className="mb-1.5 text-xs text-muted">
              Submitted scores are locked. Reopen one if the evaluator needs to change a score.
            </p>
            {c.evaluators
              .filter((e) => e.submitted)
              .map((e) => (
                <div key={e.userId} className="flex items-center gap-2 py-1 text-[13px]">
                  <span className="flex-1">{e.name}</span>
                  <Button size="sm" variant="ghost" onClick={() => setReopening({ userId: e.userId, name: e.name })}>
                    Reopen scores
                  </Button>
                </div>
              ))}
          </div>
        )}
      </Card>
      <Dialog
        open={!!reopening}
        onClose={() => setReopening(null)}
        title={reopening ? `Reopen ${reopening.name}'s scores?` : ''}
        description="Their scores unlock and leave the comparison until they submit again."
        footer={
          <>
            <Button variant="secondary" onClick={() => setReopening(null)}>
              Cancel
            </Button>
            <Button onClick={() => reopen.mutate()} loading={reopen.isPending} disabled={reason.trim().length < 5}>
              Reopen
            </Button>
          </>
        }
      >
        <Field label="Reason" hint="Kept on the record. At least 5 characters." htmlFor="reopen-reason">
          <Input id="reopen-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Dialog>
    </div>
  );
}
